/**
 * WAF billing catalog (Owl) — currently just the power-up (add-on product)
 * category taxonomy, used to group the CDN platform list by category.
 * Service-scoped, `GET /services/:service_id/waf/billing/powerup-categories`.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/**
 * Metered feature usage vs. plan quota over a date range — the overview quota
 * cards. `GET /services/:service_id/waf/billing/feature-usage?start_time=&end_time=`
 * (Owl `billing-go/v2/customer_feature/usage`).
 */
export interface WafFeatureUsage {
  mcdn_integration_quota: number;
  mcdn_integration_usage: number;
  dns_record_quota: number;
  dns_record_usage: number;
  cdn_request_usage: number;
  cdn_request_quota: number;
  cdn_traffic_usage: number;
  cdn_traffic_quota: number;
  dns_query_usage: number;
  dns_query_quota: number;
  stream_view_usage: number;
  stream_view_quota: number;
}

/** Feature usage vs. quota over a unix-second date range. */
export function useWafFeatureUsage(
  serviceId: string | undefined,
  startTime: number,
  endTime: number,
) {
  const params = { start_time: startTime, end_time: endTime };
  return useQuery<WafFeatureUsage>({
    queryKey: wafKeys.featureUsage(serviceId ?? '', params),
    enabled: !!serviceId && !!startTime && !!endTime,
    staleTime: 5 * 60_000,
    queryFn: async () => unwrap<WafFeatureUsage>(await serviceApiV3.get(`/services/${serviceId}/waf/billing/feature-usage`, { params })),
  });
}

/** The customer's credit balance behind the billing card. `GET /waf/billing/credit`. */
export interface WafCustomerCredit {
  unbilled: number;
  used: number;
  limit: number;
  free_credit: number;
  free_credit_expired_time: number | null;
  free_credit_expired: number;
}

/** The customer's credit balance (service-scoped, no range). */
export function useWafCredit(serviceId: string | undefined) {
  return useQuery<WafCustomerCredit>({
    queryKey: wafKeys.credit(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<WafCustomerCredit>(
        await serviceApiV3.get(`/services/${serviceId}/waf/billing/credit`),
      ),
  });
}

/**
 * One node of the power-up category catalog. A GROUP header carries `title` +
 * `child_category` (no `id`/`type`); a LEAF carries `id` + `type` (e.g. "CDN")
 * and no children. A CDN platform's `category_id` matches a LEAF `id`.
 */
export interface WafPowerUpCategory {
  id?: number;
  title: string;
  type?: string;
  url_param: string;
  child_category?: WafPowerUpCategory[];
}

/** Power-up (add-on product) category catalog. */
export function useWafPowerUpCategories(serviceId: string | undefined) {
  return useQuery<WafPowerUpCategory[]>({
    queryKey: wafKeys.powerUpCategories(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<WafPowerUpCategory[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/billing/powerup-categories`),
      ) ?? [],
  });
}

/** Flatten the category tree into an id → display-title map (for grouping platforms). */
export function powerUpCategoryNames(
  cats: WafPowerUpCategory[] | undefined,
  into = new Map<number, string>(),
): Map<number, string> {
  for (const c of cats ?? []) {
    if (c.id) into.set(c.id, c.title || c.type || String(c.id));
    if (c.child_category?.length) powerUpCategoryNames(c.child_category, into);
  }
  return into;
}
